import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '../../api/nocode';
import { useAuth } from '../../context/AuthContext';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';

const DAYS = [
  { day: 1, title: 'The Christian', subtitle: 'Mindset & Calling' },
  { day: 2, title: 'The Creator', subtitle: 'Writing' },
  { day: 3, title: 'The Caretaker', subtitle: 'Publishing' },
  { day: 4, title: 'The Connector', subtitle: 'Marketing' },
  { day: 5, title: 'Compass Summary', subtitle: 'Review & Export' },
];

export default function Campfire() {
  const { user } = useAuth();
  const [contents, setContents] = useState({});
  const [settings, setSettings] = useState({});
  const [activeDay, setActiveDay] = useState(1);
  const [loading, setLoading] = useState(true);
  const [unlockedDays, setUnlockedDays] = useState([]);
  const [devMode, setDevMode] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const location = useLocation();

  const fbGroupUrl = "https://www.facebook.com/groups/1316325730623969";

  // Current Time Fetcher (for displaying local time to campers)
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [contentRes, settingsRes] = await Promise.all([
          api.get('camp_content_gwacamp', { block: 'campfire' }),
          api.get('settings_gwacamp')
        ]);
        
        const contentData = Array.isArray(contentRes) ? contentRes : contentRes.data || [];
        const map = {};
        contentData.forEach(c => { map[c.day] = c; });
        setContents(map);

        const settingsData = Array.isArray(settingsRes) ? settingsRes : settingsRes.data || [];
        const sMap = {};
        settingsData.forEach(s => {
          if (s.setting_key) sMap[s.setting_key] = s.value;
        });
        setSettings(sMap);

        // Determine unlocked days
        const unlocked = [];
        const now = new Date();
        const startDateStr = sMap.camp_start || '2026-07-13'; // Default fallback
        const startDate = new Date(`${startDateStr}T00:00:00-04:00`); // Ensure EST start

        for (let i = 1; i <= 5; i++) {
          const block = map[i];
          let isUnlocked = false;
          if (block && block.unlock_time) {
            isUnlocked = now >= new Date(block.unlock_time);
          } else {
            // Fallback unlock logic (7:00 PM EST each day)
            const unlockTime = new Date(startDate.getTime());
            unlockTime.setDate(startDate.getDate() + (i - 1));
            unlockTime.setHours(19, 0, 0, 0); // 19:00 -> 7:00 PM
            isUnlocked = now >= unlockTime;
          }
          // Unlock if time has passed, devMode is on, or the specific user was granted early access
          if (isUnlocked || devMode || user?.early_access == 1) {
            unlocked.push(i);
          }
        }
        
        setUnlockedDays(unlocked);

        // Handle active day selection from URL
        const params = new URLSearchParams(location.search);
        const urlDay = parseInt(params.get('day'));
        
        if (urlDay && urlDay >= 1 && urlDay <= 5) {
          setActiveDay(urlDay);
        } else {
          setActiveDay(1);
        }
      } catch (err) {
        console.error("Failed to fetch campfire content:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [location.search, devMode, user?.early_access]);

  if (loading) {
    return (
      <div className="p-8 text-center text-muted flex flex-col items-center justify-center min-h-[400px]">
        <SafeIcon name="Loader" className="animate-spin text-4xl text-amber mb-4" />
        <p>Loading Campfire...</p>
      </div>
    );
  }

  const content = contents[activeDay];
  const isUnlocked = unlockedDays.includes(activeDay);

  return (
    <div className="max-w-6xl mx-auto flex flex-col lg:flex-row gap-8 pb-20">
      {/* Sidebar Navigation */}
      <div className="w-full lg:w-72 shrink-0 space-y-4 no-print">
        <div className="bg-[#EAF1E6] border-l-4 border-forest-green p-4 rounded-r-md mb-6">
          <h1 className="text-xl font-bold text-dark-green flex items-center gap-2">
            <SafeIcon name="Flame" className="text-amber" /> Evening Campfire
          </h1>
          <p className="text-xs mt-1 text-forest-green font-medium">Daily Reflection</p>
        </div>
        
        <nav className="space-y-2">
          {DAYS.map((d) => {
            const unlocked = unlockedDays.includes(d.day);
            const active = activeDay === d.day;
            return (
              <button
                key={d.day}
                onClick={() => setActiveDay(d.day)}
                className={`w-full text-left p-4 rounded-md border transition-all flex items-center justify-between
                  ${active ? 'bg-forest-green border-forest-green text-white shadow-md' : 
                    'bg-white border-tan hover:border-amber text-dark-green'}`}
              >
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider mb-1 opacity-80">Day {d.day}</div>
                  <div className={`font-bold ${active ? 'text-white' : 'text-dark-green'}`}>{d.title}</div>
                  <div className={`text-xs ${active ? 'text-tan' : 'text-muted'}`}>{d.subtitle}</div>
                </div>
                <div>
                  {!unlocked ? (
                    <SafeIcon name="Lock" className="text-gray-400" />
                  ) : active ? (
                    <SafeIcon name="ChevronRight" />
                  ) : (
                    <SafeIcon name="CheckCircle" className="text-amber" />
                  )}
                </div>
              </button>
            );
          })}
        </nav>

        {/* Dev Toggle */}
        {user?.role === 'admin' && (
          <div className="pt-8 text-center">
            <button 
              onClick={() => setDevMode(!devMode)} 
              className="text-xs text-muted hover:text-amber border border-dashed border-gray-300 rounded px-2 py-1"
            >
              {devMode ? 'Disable Dev Mode' : 'Dev: Unlock All Days'}
            </button>
          </div>
        )}
      </div>

      {/* Main Content Area */}
      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-center bg-white border-b border-tan pb-4 mb-6 pt-2 no-print">
          <div>
            <h2 className="text-2xl font-bold text-dark-green">Day {activeDay}: {DAYS[activeDay-1].title}</h2>
            <p className="text-muted">{DAYS[activeDay-1].subtitle}</p>
          </div>
        </div>

        {content && (content.is_active == 1 || content.is_active === true) && isUnlocked ? (
          <Card className="text-center py-12 space-y-6 border-tan shadow-sm animate-fadeIn">
            <SafeIcon name="Flame" className="text-6xl text-amber mx-auto" />
            
            <div className="max-w-xl mx-auto space-y-6">
              <h2 className="text-2xl font-bold text-dark-green">Day {activeDay} Reflection</h2>
              <p className="text-forest-green text-xl italic font-medium whitespace-pre-wrap leading-relaxed">
                "{content.prompt}"
              </p>
              {content.encouragement_note && (
                <div className="mt-6 border-t border-tan pt-6 text-left space-y-4">
                  {(content.encouragement_note || '').split('\n\n').map((paragraph, idx) => (
                    <p key={idx} className="text-lg text-dark-green leading-relaxed whitespace-pre-wrap">
                      {paragraph}
                    </p>
                  ))}
                </div>
              )}
            </div>
            
            <div className="pt-8 border-t border-tan max-w-md mx-auto">
              <p className="mb-4 text-sm text-muted">Join the community around the virtual campfire to share your answer and encourage others.</p>
              <a href={content.fb_link || fbGroupUrl} target="_blank" rel="noopener noreferrer">
                <Button className="w-full bg-[#1877F2] hover:bg-[#1864D9] text-white border-none focus:ring-[#1877F2]">
                  <SafeIcon name="Facebook" /> Share in Facebook Group
                </Button>
              </a>
            </div>
          </Card>
        ) : (
          <Card className="text-center py-16 bg-gray-50 border-dashed border-2 animate-fadeIn relative overflow-hidden">
            <div className="absolute top-0 right-0 m-4 bg-white px-3 py-1.5 rounded shadow-sm border border-gray-200 flex flex-col items-end">
              <span className="text-[10px] uppercase font-bold text-gray-400 tracking-wider">Current System Time</span>
              <span className="text-sm font-mono text-dark-green font-medium">
                {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </span>
            </div>

            <div className="w-20 h-20 bg-[#EDE0CC] rounded-full flex items-center justify-center mb-6 mx-auto mt-4">
              <SafeIcon name="Lock" className="w-9 h-9 text-[#2D5016]" />
            </div>
            <h3 className="text-2xl font-bold text-dark-green mb-2">Campfire Not Yet Open</h3>
            <p className="text-muted max-w-sm mx-auto leading-relaxed">
              This evening conversation unlocks at <strong>7:00 pm EST</strong> on Day {activeDay} — {DAYS[activeDay - 1].title}.
            </p>

            {content && content.prompt && (
              <div className="mt-8 p-6 bg-white rounded-xl shadow-sm border border-tan max-w-lg mx-auto text-left relative">
                <div className="absolute top-0 left-0 w-full h-1 bg-amber rounded-t-xl"></div>
                <h4 className="text-xs font-bold text-forest-green uppercase tracking-wider mb-2 flex items-center gap-2">
                  <SafeIcon name="Flame" className="w-4 h-4 text-amber" />
                  Consider this beforehand
                </h4>
                <p className="text-dark-green text-lg italic whitespace-pre-wrap">"{content.prompt}"</p>
              </div>
            )}

            <div className="mt-10 pt-8 border-t border-gray-200 max-w-sm mx-auto">
              <p className="text-sm text-muted mb-4">
                Make sure you're in the Facebook group so you don't miss the discussion when it unlocks!
              </p>
              <a href={fbGroupUrl} target="_blank" rel="noopener noreferrer">
                <Button className="w-full bg-[#1877F2] hover:bg-[#1864D9] text-white border-none focus:ring-[#1877F2]">
                  <SafeIcon name="Facebook" /> Join the Facebook Group
                </Button>
              </a>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}